import { Box } from '@mui/material';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';

import { CriteriaSection } from './CriteriaSection/CriteriaSection';
import { SqlSection } from './SqlSection/SqlSection';
import { StudyPeriodSection } from './StudyPeriodSection/StudyPeriodSection';

interface DefinitionTabProps {
  /** True while Study period's own data (the definition query) is still loading. */
  isStudyPeriodLoading: boolean;
  /** True while Criteria's data (definition + concept groups) is still loading. */
  isCriteriaLoading: boolean;
}

/**
 * Definition tab body for the gen-3 cohort detail page.
 *
 * Sibling sections, each their own composed unit (component + hook in
 * matching folders): Study period, Criteria (AV-only), and — while the
 * `sql_tab_in_cb` flag is off — SQL. Flag ON moves SQL to its own
 * sibling tab (see `../SqlTab/SqlTab`) per the M4 workspace design.
 *
 * Layout: vertical stack with consistent gutters. Each card is
 * width-fluid and clamps inner content (criteria grid auto-fills).
 *
 * Study period and Criteria each get the loading flag for only the data
 * they read (see `ProjectCohortDetailPage`, gated on `cohort_ui_optimised`)
 * rather than one shared flag, so neither is held back by a call it
 * doesn't depend on. The SQL section reads from its own TanStack query and
 * tracks loading independently.
 */
export const DefinitionTab = ({ isStudyPeriodLoading, isCriteriaLoading }: DefinitionTabProps) => {
  // TODO: cleanup with sql_tab_in_cb ff
  const isSqlTabEnabled = Boolean(useFeatureFlagSplit('sql_tab_in_cb'));

  return (
    <Box
      data-testid="cohort-detail-definition-tab"
      sx={{
        width: '100%',
        minHeight: 0,
        paddingX: 6,
        paddingY: 3,
        display: 'flex',
        flexDirection: 'column',
        // 24px between section cards — same rhythm as the AD definition tab
        // and the Figma module gap.
        gap: 6,
      }}
    >
      <StudyPeriodSection isLoading={isStudyPeriodLoading} />
      <CriteriaSection isLoading={isCriteriaLoading} />
      {!isSqlTabEnabled && <SqlSection />}
    </Box>
  );
};
